import { cloneDeep, isEqual } from "lodash";
import {
  FieldName,
  FieldSchema,
  FieldValue,
  FieldValues,
  isEmpty,
  PreparedDynamicStructure,
  Schema,
  EMPTY_VALUE,
  prepareDynamicPropertyValue,
  FieldNames,
} from "../Schema";
import {
  DynamicManager,
  BasicState,
  CustomState,
  FieldsState,
  Mapping,
} from "../common/DynamicManager";
import {
  DataCardinalities,
  DataEnumSchema,
  DataTypes,
  DataVariants,
  EnumOption,
} from "../data/DataManager";
import { Widths } from "../form/FormManager";

/**
 * Render modes understood by the DisplayManager. `NORMAL` shows a single value;
 * `CUSTOM` hands the whole cell to a consumer-provided renderer. Whether a value
 * is shown on its own or as a before/after change is a Display-level `change`
 * parameter, not a per-field type.
 */
export const DisplayRenders = {
  NORMAL: "normal",
  CUSTOM: "custom",
};

export type DisplayRender = typeof DisplayRenders[keyof typeof DisplayRenders];

/**
 * Placeholder rendered for empty values when no other empty text is configured.
 */
export const DISPLAY_EMPTY_VALUE = "—";


export interface DisplaySchema {
  type?: DisplayRender;
  aliasOf?: string;
  label?: string;
  tooltip?: string;
  width?: Widths;
  displayed?: boolean;
  render?: any;
  translator?: DisplayTranslator;
}

/**
 * Display schema for a field rendered by a consumer-provided component instead of
 * the built-in formatters. The `render` function owns the whole field cell and is
 * handed the field's raw value and display state.
 */
export interface DisplayCustomSchema extends DisplaySchema {
  type: typeof DisplayRenders.CUSTOM;
  render: any;
}

export const DisplayModes = {
  NORMAL: "normal",
  RED: "red",
  GREEN: "green",
};

export type DisplayMode = typeof DisplayModes[keyof typeof DisplayModes];

export interface DisplayValue {
  value: string;
  mode: DisplayMode;
}

export type DisplayValues = DisplayValue[];

export interface DisplayFieldState extends BasicState {
  name: FieldName;
  type?: DisplayRender;
  aliasOf?: string;
  label?: string;
  tooltip?: string;
  width?: Widths;
  displayed?: boolean;
  values: DisplayValues;
}

export const defaultDisplayFieldState: DisplayFieldState = {
  loading: true,
  name: "default",
  aliasOf: `default_${Math.random().toString(36).substring(2, 15)}`,
  label: "",
  tooltip: "",
  width: Widths.NORMAL,
  displayed: true,
  values: [{
    value: DISPLAY_EMPTY_VALUE,
    mode: DisplayModes.NORMAL,
  }],
};

export type DisplayTranslator = (
  value: FieldValue,
  values?: FieldValues
) => (Promise<string[]> | string[]);

export interface DisplayState extends CustomState {
  values: Record<FieldName, DisplayValues>;
}

export const defaultDisplayState: DisplayState = {
  loading: true,
  values: {},
};

/**
 * Manager for the read-only presentation of a schema. Unlike the FormManager it
 * runs no data or validation logic: it takes values as-is and turns them into
 * display strings, resolving enum labels from each field's `data.options`
 * (executed as a dynamic structure so value-dependent options work too).
 *
 * It also derives per-field change information by comparing each value against
 * the matching entry in the `originalValues` field, which powers the audit-trail
 * use case. Presentation metadata falls back to the `form` aspect, so a field
 * only needs a `display` block when its read view differs from the edit form.
 */
export class DisplayManager extends DynamicManager<DisplayFieldState, DisplayState> {
  /**
   * Custom state summarizing the display: the current values and the list of
   * fields that differ from their `originalValues` snapshot.
   */
  customState: DisplayState = defaultDisplayState;

  /**
   * Default placeholder rendered for empty values across the whole schema.
   * Overridden per field by `display.emptyText`. Set this to use a longer
   * phrase (e.g. "Not specified") for a whole view in one place.
   */
  emptyText: string = DISPLAY_EMPTY_VALUE;

  /**
   * @param schema - Schema that the DisplayManager is working with.
   */
  constructor(schema: Schema = {}) {
    super(schema);
  }

  async init(
    currentValues: FieldValues,
    oldValues?: FieldValues,
  ) {
    await this.updateState(
      {
        currentValues: cloneDeep(currentValues),
        oldValues: oldValues && cloneDeep(oldValues),
      },
      true
    );
    await this.setLoading(false);
    return this;
  }

  prepareTranslator(
    field: FieldSchema
  ): {
    translator: DisplayTranslator;
    fields: FieldNames;
  } {
    if (field.display?.translator) {
      return {
        translator: field.display.translator as DisplayTranslator,
        fields: [field.name],
      };
    }

    let translator: DisplayTranslator = async (value) => {
      if (
        !value ||
        !["string", "number", "boolean"].includes(typeof value)
      ) {
        return [this.emptyText];
      }
      return [value.toString() as string];
    };
    let fields = [field.name];

    if (field.data?.variant === DataVariants.ENUM) {
      const preparedOptions = prepareDynamicPropertyValue(
        (field.data as DataEnumSchema)?.options || []
      );
      translator = async (
        value: FieldValue,
        values: FieldValues = {}
      ) => {
        const options = (await preparedOptions.getter(values))
          .map((option: EnumOption<FieldValue>) => {
            if (typeof option === "string") {
              return {
                value: option,
                label: option,
              }
            }
            return option;
          });
        const option = options.find(
          (option) => isEqual(option.value, value)
        );
        return [
          option?.label ?? this.emptyText
        ];
      };
        fields = [field.name, ...preparedOptions.fields];
    } else {
      if (field.data?.type === DataTypes.BOOLEAN) {
        translator = async (value) => [(value as boolean) ? "Yes" : "No"];
        fields = [field.name];
      }

      if (field.data?.type === DataTypes.NUMBER) {
        translator = async (value) => [(value as number).toString()];
        fields = [field.name];
      }
      if (field.data?.type === DataTypes.OBJECT) {
        translator = async (value) => {
          let preparedValue = this.emptyText;
          if (value && typeof value === "object") {
            const insideValues = Object.values(value)
              .filter((innerValue) => {
                return typeof innerValue === "string" ||
                  typeof innerValue === "number" ||
                  typeof innerValue === "boolean";
              });
            if (insideValues.length) {
              preparedValue = insideValues
                .map(String)
                .join(", ");
            }
          }
          return [preparedValue];
        };
      }
    }

    if (field.data?.cardinality === DataCardinalities.MULTIPLE) {
      const previousTranslator = translator;
      translator = async (value, values) => {
        const preparedValues: string[] = [];
        if (Array.isArray(value) && value.length) {
          for (const item of value) {
            preparedValues.push(
              ...(await previousTranslator(item, values))
            );
          }
        }
        return preparedValues;
      };
    }

    return {
      translator,
      fields,
    };
  };

  prepareValuesExtractor(
    field: FieldSchema,
    path: string
  ): PreparedDynamicStructure<(string | typeof EMPTY_VALUE)[] | undefined> {
    const {translator, fields} = this.prepareTranslator(field);
    return {
      getter: async (values) => {
        const pathValues = values[path] as FieldValues;
        if (!pathValues) {
          return undefined;
        }
        if (isEmpty(pathValues[field.name])) {
          return [EMPTY_VALUE];
        }
        return await translator(pathValues[field.name], pathValues);
      },
      fields: [path, ...fields],
    };
  }

  prepareValuesExtractors(
    field: FieldSchema,
  ): PreparedDynamicStructure<(string | typeof EMPTY_VALUE)[] | undefined>[] {
    return [
      this.prepareValuesExtractor(field, "currentValues"),
      this.prepareValuesExtractor(field, "oldValues"),
    ];
  }

  prepareModeProcessor(
    [
      currentValuesExtractor,
      oldValuesExtractor
    ]: PreparedDynamicStructure<(string | typeof EMPTY_VALUE)[] | undefined>[]
  ): PreparedDynamicStructure<DisplayValues> {
    return {
      getter: async (values) => {
        const currentValues = await currentValuesExtractor.getter(values);
        const oldValues = await oldValuesExtractor.getter(values);
        const result: DisplayValues = [];
        if (currentValues !== undefined && oldValues === undefined) {
          currentValues.forEach((value) => {
            result.push({
              value: value ?? this.emptyText,
              mode: DisplayModes.NORMAL,
            });
          });
          return result;
        }
        if (
          oldValues !== undefined &&
          currentValues !== undefined
        ) {
          const allValues = Array.from(new Set([
            ...oldValues,
            ...currentValues
          ]));
          allValues.forEach((value) => {
            if (oldValues.includes(value)) {
              if (currentValues.includes(value)) {
                result.push({
                  value: value ?? this.emptyText,
                  mode: DisplayModes.NORMAL,
                });
              } else {
                result.push({
                  value: value ?? this.emptyText,
                  mode: DisplayModes.RED,
                });
              }
            } else {
              if (currentValues.includes(value)) {
                result.push({
                  value: value ?? this.emptyText,
                  mode: DisplayModes.GREEN,
                });
              }
            }
          });
          return result;
        }
        return result;
      },
      fields: Array.from(new Set([
        ...currentValuesExtractor.fields,
        ...oldValuesExtractor.fields
      ])),
    };
  }

  prepareValuesGenerator(
    field: FieldSchema,
  ): PreparedDynamicStructure<DisplayValues> {
    const valuesExtractors = this.prepareValuesExtractors(field);

    return this.prepareModeProcessor(valuesExtractors);
  }
  /**
   * Generates a mapping for a display field's state. Metadata falls back to the
   * form aspect, the raw and formatted values are derived from the current
   * values, and the previous value / changed flag are derived from the
   * `originalValues` field.
   *
   * @param field - Field schema to generate the mapping for.
   * @returns A mapping for the display field state.
   */
  getMapping(field: FieldSchema): Mapping<DisplayFieldState> {
    const mainName = typeof field.display?.aliasOf === 'string' ?
      field.display?.aliasOf :
      field.name;
    const mainSchema = this.schema[mainName];
    const source: FieldSchema = {
      ...mainSchema,
      ...field,
      data: {
        ...mainSchema?.data,
        ...field.data
      },
      display: {
        ...mainSchema?.display,
        ...field.display,
        aliasOf: mainName,
      },
      form: {
        ...mainSchema?.form,
        ...field.form
      },
    };

    return {
      name: field.name,
      aliasOf: mainName,
      type: source.display?.type,
      label: source.display?.label ?? source.form?.label ?? source.name,
      tooltip: source.display?.tooltip ?? source.form?.tooltip,
      width: source.display?.width ?? source.form?.width,
      displayed: source.display?.displayed ?? source.form?.displayed ?? false,
      values: this.prepareValuesGenerator(source),
    };
  }

  /**
   * Updates the custom state of the DisplayManager based on the current field
   * states, gathering the current values and the list of changed fields.
   */
  updateCustomState(): void {
    const values: Record<FieldName, DisplayValues> = {};
    for (const [fieldName, fieldState] of Object.entries(this.state)) {
      values[fieldName] = fieldState.values;
    }
    this.customState = {
      loading: this.loading,
      values,
    };
  }

  /**
   * Retrieves the current custom state of the display, including the current
   * values and the list of changed fields.
   *
   * @returns The current custom state of the display.
   */
  public getDisplayState(): DisplayState {
    return cloneDeep(this.customState);
  }

  /**
   * Retrieves the current state of all fields in the display.
   *
   * @returns An object containing the current state of all fields.
   */
  public getFieldsState(): FieldsState<DisplayFieldState> {
    return cloneDeep(this.state);
  }

  /**
   * Retrieves the current display state of a specific field.
   *
   * @param name - The name of the field to retrieve the state for.
   * @returns The current state of the field, or a default state if not available.
   */
  public getFieldDisplayState(name: FieldName): DisplayFieldState {
    return this.getFieldState(name) ?? defaultDisplayFieldState;
  }
}
